import { authedApiFetch } from 'utils/Api';

/**
 * Service for AI-drafted LP outreach emails (alps-be CR-1716).
 *
 * This service is the single place that turns API failures into user-facing
 * messages, so callers can surface `error.message` directly and be safe by
 * construction.
 */
const emailDraftsService = {
	/**
	 * Generate a drafted outreach email for the record an alert points at. The
	 * backend resolves the account itself (a fundraising via its AIM__Account__c
	 * lookup), so callers forward the alert's own Salesforce type and id.
	 * @param {string} tenantId - Tenant id (from the route)
	 * @param {Object} args
	 * @param {string} args.topic - One of OUTREACH_TOPICS values
	 * @param {string} args.salesforceObjectType - 'Account' or 'AIM__Fundraising__c'
	 * @param {string} args.salesforceObjectId - Salesforce record id
	 * @returns {Promise<Object>} - { topic, account_id, contact, context, email, quality_warnings, attempts }
	 * @throws {Error} with a message safe to show the user
	 */
	generateDraft: async (
		tenantId,
		{ topic, salesforceObjectType, salesforceObjectId }
	) => {
		let response;

		try {
			response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/email_drafts`,
				method: 'POST',
				payload: {
					topic,
					salesforce_object_type: salesforceObjectType,
					salesforce_object_id: salesforceObjectId,
				},
				returnResponse: true,
			});
		} catch (error) {
			// authedApiFetch stringifies transport/token failures into a
			// developer-facing message; never surface that to the user.
			console.error('Draft request failed to reach the API:', error);

			throw new Error(
				'Unable to reach the drafting service. Please try again.'
			);
		}

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));

			// Backend messages here are written for end users
			// (e.g. "No contact found for this account"), so pass them through.
			throw new Error(body.error || 'Unable to generate outreach draft.');
		}

		try {
			return await response.json();
		} catch (error) {
			console.error('Draft response was not valid JSON:', error);

			throw new Error('Unable to generate outreach draft.');
		}
	},

	/**
	 * Apply a quick edit (preset type or custom instruction) to an existing
	 * drafted email. The call is stateless: the current subject and body are sent
	 * to the backend and the revised body is returned.
	 * @param {string} tenantId
	 * @param {Object} args
	 * @param {string} args.subject - The existing email subject
	 * @param {string} args.body - The existing email body
	 * @param {string|string[]} args.preset_types - One or more QUICK_EDIT_TYPES, or undefined for custom
	 * @param {string} [args.custom_instruction] - Optional custom edit instruction
	 * @returns {Promise<Object>} - { email: { subject, body } }
	 */
	quickEdit: async (tenantId, { subject, body, preset_types, custom_instruction }) => {
		let response;

		try {
			response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/email_drafts/quick_edit`,
				method: 'POST',
				payload: {
					subject,
					body,
					preset_types,
					custom_instruction,
				},
				returnResponse: true,
			});
		} catch (error) {
			console.error('Quick edit request failed to reach the API:', error);

			throw new Error(
				'Unable to reach the drafting service. Please try again.'
			);
		}

		if (!response.ok) {
			const body = await response.json().catch(() => ({}));

			throw new Error(body.error || 'Unable to apply quick edit.');
		}

		try {
			return await response.json();
		} catch (error) {
			console.error('Quick edit response was not valid JSON:', error);

			throw new Error('Unable to apply quick edit.');
		}
	},
};

export default emailDraftsService;
